import React, { ReactElement, useEffect, useState } from 'react';
import { HistoriquePrixProduit, HistoriquePrixProduitAvecDateFin } from '../../../../../../common/src/entities';
import { useField } from 'formik';
import { Button, Dropdown, Form, Modal } from 'react-bootstrap';
import { Colonne } from '../../Utils/TypesTableSimple';
import { QueryResult, QueryStatus } from 'react-query';
import { FetchMoreOptions, RefetchOptions } from 'react-query/types/core/query';
import TableSimple from '../../Utils/TableSimple';
import { CellProps } from 'react-table';
import DetailTable from './DetailTable';
import PopoverSimple from '../../../Common/PopoverSimple';
import { DateTime } from 'luxon';
import CreerProduitPrix from './CreerHistoriquePrix';
import DetailsHistoriquePrixProduit from './DetailsHistoriquePrix';
import { useDeleteEntite } from '../../../../Utils/api';
import { clientPrice, getTotalCommission, getTotalSurfacturation } from '../UtilsFunctionsProduits';
import { MyPaper } from '../../Utils/MyPaper';

function getTheLastPrice(historyPrice: HistoriquePrixProduit[]): HistoriquePrixProduit | undefined {
    if (historyPrice.length === 0) {
        return {
            id: 0,
            date_debut: '',
            facturation: {
                b_taxable: false,
                methode_facturation: '',
                prix_de_vente_fournisseur: 0,
                valeur_commission_dollar: 0,
                valeur_commission_pourcentage: 0,
                valeur_surfacturation_dollar: 0,
                valeur_surfacturation_pourcentage: 0,
            },
        } as HistoriquePrixProduit;
    }
    return latestDate(historyPrice);
}

function latestDate(historyPrice: HistoriquePrixProduit[]) {
    const latestHistory = historyPrice.reduce((plusRecent, objet) => {
        return new Date(objet.date_debut) > new Date(plusRecent.date_debut) ? objet : plusRecent;
    }, historyPrice[0]);
    return historyPrice[historyPrice.indexOf(latestHistory)];
}

const AffichageAjout = (props: {
    values: HistoriquePrixProduit[];
    addNewPrixProduit: (newItem: HistoriquePrixProduit) => void;
}): ReactElement => {
    const [showModal, setShowModal] = useState(false);
    const formProps = {
        modal: true,
        succes: (h: HistoriquePrixProduit) => {
            props.addNewPrixProduit(h);
            setShowModal(false);
        },
    };
    return (
        <>
            <Button variant="outline-primary" size="sm" onClick={() => setShowModal(true)}>
                Ajouter un prix
            </Button>
            {/* modal création prix produit */}
            <Modal
                scrollable
                show={showModal}
                onHide={() => {
                    setShowModal(false);
                }}
            >
                <CreerProduitPrix {...formProps} initialValues={getTheLastPrice(props.values)} />
            </Modal>
        </>
    );
};

export default function PrixProduitTable(): ReactElement {
    const [formProps, setFormProps] = useState({});
    const [localHistoriquePrix, setLocalHistoriquePrix] = useState<HistoriquePrixProduit[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [field, _, helpers] = useField<HistoriquePrixProduit[]>('historique_prix_produit');
    const [deletePrix] = useDeleteEntite(HistoriquePrixProduit);
    let fieldValue = field.value;

    useEffect(() => {
        if (fieldValue !== localHistoriquePrix) {
            setLocalHistoriquePrix([...fieldValue]);
        }
    }, [fieldValue]);

    const openModifyItemModal = (id: number) => {
        setLocalHistoriquePrix((prevState) => {
            setShowModal(true);
            setFormProps({
                id: id,
                valeursInitiales: prevState.find((h: HistoriquePrixProduit) => h.id == id),
                modal: true,
            });
            return prevState;
        });
    };

    const modifyItem = (data: HistoriquePrixProduit) => {
        setShowModal(false);
        setFormProps({});
        setLocalHistoriquePrix((prevState) => {
            const updatedLocalHistoriquePrix = prevState.map((item) =>
                item.id == data.id ? { ...item, ...data } : item,
            );
            helpers.setValue(updatedLocalHistoriquePrix as HistoriquePrixProduit[]);
            return updatedLocalHistoriquePrix;
        });
    };

    const addNewPrixProduit = (newItem: HistoriquePrixProduit) => {
        setLocalHistoriquePrix([newItem, ...localHistoriquePrix]);
        setShowModal(false);
        helpers.setValue([newItem, ...localHistoriquePrix]);
    };

    const removePrixProduit = (id: number) => {
        setLocalHistoriquePrix((prevState) => {
            const updatedLocalHistoriquePrix = prevState.filter((item) => item.id !== id);
            helpers.setValue(updatedLocalHistoriquePrix);
            return updatedLocalHistoriquePrix;
        });
        deletePrix(id);
    };

    function formatDate(timestampISO: string) {
        if (!timestampISO) {
            return 'Indéfinie';
        }
        const dateTime = DateTime.fromISO(timestampISO, { zone: 'utc' });
        return dateTime.toFormat('dd-MMM-yy (EEE)').replaceAll('.', '');
    }

    const cellStyle = {
        textDecoration: 'underline',
    };

    const colonnes: Colonne<HistoriquePrixProduitAvecDateFin>[] = [
        {
            Header: 'Txs',
            Cell: ({ row }) => {
                return (
                    <Form.Check readOnly className="ml-2" type="checkbox" checked={row.original.facturation?.b_taxable} />
                );
            },
            disableSortBy: true,
        },
        {
            Header: 'Prix vente fournisseur',
            accessor: (row) => {
                return `${Number(row.facturation?.prix_de_vente_fournisseur).toFixed(2)}$`;
            },
            disableSortBy: true,
        },
        {
            Header: 'Méthode facturation',
            accessor: (row) => {
                return row.facturation?.methode_facturation;
            },
            disableSortBy: true,
        },
        {
            Header: 'C%',
            accessor: (row) => {
                return Number(row.facturation?.valeur_commission_pourcentage).toFixed(0) + '%';
            },
            disableSortBy: true,
        },
        {
            Header: 'C$',
            accessor: (row) => {
                return Number(row.facturation?.valeur_commission_dollar).toFixed(2) + '$';
            },
            disableSortBy: true,
        },
        {
            Header: 'Total commission',
            accessor: (row) => {
                return `${Number(getTotalCommission(row)).toFixed(2)}$`;
            },
            disableSortBy: true,
        },
        {
            Header: 'Prix courtier',
            accessor: (row) => {
                return `${(
                    Number(row.facturation?.prix_de_vente_fournisseur) - Number(getTotalCommission(row))
                ).toFixed(2)}$`;
            },
            disableSortBy: true,
        },
        {
            Header: 'S%',
            accessor: (row) => {
                return Number(row.facturation?.valeur_surfacturation_pourcentage).toFixed(0) + '%';
            },
            disableSortBy: true,
        },
        {
            Header: 'S$',
            accessor: (row) => {
                return Number(row.facturation?.valeur_surfacturation_dollar).toFixed(2) + '$';
            },
            disableSortBy: true,
        },
        {
            Header: 'Total Surfacturation',
            accessor: (row) => {
                return `${Number(getTotalSurfacturation(row)).toFixed(2)}$`;
            },
            disableSortBy: true,
        },
        {
            Header: 'Prix client',
            Cell: function CellulePrixClient({ row }: CellProps<HistoriquePrixProduit>) {
                return (
                    <>
                        {' '}
                        <PopoverSimple
                            inList
                            texte={
                                <>
                                    <DetailTable
                                        title="Détail du prix client"
                                        prix_de_vente={Number(row.original.facturation?.prix_de_vente_fournisseur)}
                                        surfacturation={Number(getTotalSurfacturation(row.original)).toFixed(2)}
                                    />
                                </>
                            }
                        >
                            <span style={cellStyle}>{Number(clientPrice(row.original)).toFixed(2)}</span>
                        </PopoverSimple>
                    </>
                );
            },
            disableSortBy: true,
        },
        {
            Header: 'Profit total',
            accessor: (row) => {
                return `${(Number(getTotalSurfacturation(row)) + Number(getTotalCommission(row))).toFixed(2)}$`;
            },
            disableSortBy: true,
        },
        {
            Header: 'Date début',
            accessor: (row) => {
                return formatDate(row.date_debut);
            },
            disableSortBy: true,
        },
        {
            Header: 'Date fin',
            accessor: (row) => {
                return formatDate(row.date_fin);
            },
            disableSortBy: true,
        },
    ];

    const resultat: QueryResult<HistoriquePrixProduitAvecDateFin[], any> = {
        data: fieldValue.map((value, index, items) => {
            if (index === 0) {
                return { ...value, date_fin: '' };
            } else {
                return {
                    ...value,
                    date_fin: DateTime.fromISO(items[index - 1].date_debut)
                        .minus({ days: 1 })
                        .toFormat('yyyy-MM-dd'),
                };
            }
        }),
        error: null,
        isLoading: false,
        canFetchMore: undefined,
        clear: function (): void {
            throw new Error('Function not implemented.');
        },
        failureCount: 0,
        fetchMore: function (
            fetchMoreVariable?: unknown,
            options?: FetchMoreOptions | undefined,
        ): Promise<HistoriquePrixProduitAvecDateFin[] | undefined> {
            throw new Error('Function not implemented.');
        },
        isError: false,
        isFetched: false,
        isFetchedAfterMount: false,
        isFetching: false,
        isIdle: false,
        isInitialData: false,
        isPreviousData: false,
        isPlaceholderData: false,
        isStale: false,
        isSuccess: false,
        refetch: function (
            options?: RefetchOptions | undefined,
        ): Promise<HistoriquePrixProduitAvecDateFin[] | undefined> {
            throw new Error('Function not implemented.');
        },
        remove: function (): void {
            throw new Error('Function not implemented.');
        },
        status: QueryStatus.Success,
        updatedAt: 0,
    };

    return (
        <MyPaper>
            <TableSimple
                colonnes={colonnes}
                resultat={resultat}
                lien="id"
                hover
                affichageMenu={(historique_prix_produit: HistoriquePrixProduit) => (
                    <>
                        <Dropdown.Item onClick={() => openModifyItemModal(historique_prix_produit.id)}>
                            Modifier
                        </Dropdown.Item>
                        <Dropdown.Item
                            onClick={() => {
                                removePrixProduit(historique_prix_produit.id);
                            }}
                            className="text-danger"
                        >
                            Supprimer
                        </Dropdown.Item>
                    </>
                )}
                etatInitial={{
                    filters: [
                        {
                            id: 'actif',
                            value: true,
                        },
                    ],
                }}
                cantNavigate={true}
            />
            <Modal
                scrollable
                show={showModal}
                onHide={() => {
                    setShowModal(false);
                    setFormProps({});
                }}
            >
                {formProps && (
                    <DetailsHistoriquePrixProduit
                        onBeforeSave={(data: HistoriquePrixProduit) => modifyItem(data)}
                        {...formProps}
                    />
                )}
            </Modal>
            <hr /> {/* Ligne de séparation */}
            <AffichageAjout values={localHistoriquePrix} addNewPrixProduit={addNewPrixProduit} />
        </MyPaper>
    );
}
